import React, { useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Printer, Download, ChevronDown, ChevronUp, AlertCircle, AlertTriangle, CheckCircle, Info, HelpCircle, Clock, BookOpen, Globe } from 'lucide-react';
import PageWrapper from '../components/PageWrapper';
import Breadcrumbs from '../components/Breadcrumbs';
import CollapsibleSection, { FAQBox, CalloutBox, StepList } from '../components/CollapsibleSection';
import { pelvicFloorPTContent, bcgTherapyContent, catheterFlushingContent, nephrostomyContent } from '../data/enhancedEducationContent';
import RelatedVideos from '../components/RelatedVideos';

// Map topic IDs to their content and metadata
const topicData = {
  'pelvic-floor-pt': {
    content: pelvicFloorPTContent,
    title: 'Pelvic Floor Physical Therapy',
    description: 'Learn about pelvic floor PT, exercises, and when to seek this treatment',
    category: 'bladder-health',
    readingTime: 8,
    relatedTags: ['pelvic-floor', 'kegel', 'incontinence', 'bladder', 'exercises'],
    icon: '🏃'
  },
  'bcg-therapy': {
    content: bcgTherapyContent,
    title: 'BCG Therapy for Bladder Cancer',
    description: 'Understanding BCG immunotherapy treatment, what to expect, and care instructions',
    category: 'cancer',
    readingTime: 10,
    relatedTags: ['bcg', 'bladder-cancer', 'immunotherapy', 'intravesical', 'treatment'],
    icon: '💉'
  },
  'catheter-flushing': {
    content: catheterFlushingContent,
    title: 'Catheter Flushing & Troubleshooting',
    description: 'Step-by-step guide for catheter irrigation and solving common problems',
    category: 'procedures',
    readingTime: 7,
    relatedTags: ['catheter', 'flushing', 'foley', 'irrigation', 'home-care'],
    icon: '🔧'
  },
  'nephrostomy-care': {
    content: nephrostomyContent,
    title: 'Nephrostomy Tube Care at Home',
    description: 'Complete guide to caring for your nephrostomy tube, including daily care and warning signs',
    category: 'kidney-health',
    readingTime: 9,
    relatedTags: ['nephrostomy', 'kidney', 'tube-care', 'drainage', 'home-care'],
    icon: '🏥'
  }
};

// Printable PDF Generator Component
const PrintableGuide = ({ topic, content }) => {
  const printRef = useRef(null);

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const printContent = printRef.current?.innerHTML || '';
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${topic.title} - Uro2Go Patient Guide</title>
          <style>
            * { box-sizing: border-box; }
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              padding: 24px;
              max-width: 800px;
              margin: 0 auto;
              line-height: 1.6;
              color: #1a1a1a;
            }
            .header { 
              border-bottom: 3px solid #0B5D6E; 
              padding-bottom: 16px; 
              margin-bottom: 24px;
            }
            h1 { 
              color: #0B5D6E; 
              font-size: 24px; 
              margin: 0 0 8px 0;
            }
            .subtitle { 
              color: #666; 
              font-size: 14px; 
            }
            h2 { 
              color: #0B5D6E; 
              font-size: 18px; 
              margin: 24px 0 12px 0;
              border-bottom: 1px solid #e5e5e5;
              padding-bottom: 8px;
            }
            h3 { 
              color: #333; 
              font-size: 16px;
              margin: 16px 0 8px 0;
            }
            ul, ol { 
              margin: 8px 0 16px 20px; 
              padding: 0;
            }
            li { 
              margin-bottom: 6px; 
            }
            .warning-box { 
              background: #FEF3C7; 
              padding: 12px 16px; 
              border-left: 4px solid #F59E0B; 
              margin: 16px 0;
              border-radius: 4px;
            }
            .emergency-box { 
              background: #FEE2E2; 
              padding: 12px 16px; 
              border-left: 4px solid #DC2626; 
              margin: 16px 0;
              border-radius: 4px;
            }
            .tip-box { 
              background: #ECFDF5; 
              padding: 12px 16px; 
              border-left: 4px solid #10B981; 
              margin: 16px 0;
              border-radius: 4px;
            }
            .info-box { 
              background: #EFF6FF; 
              padding: 12px 16px; 
              border-left: 4px solid #3B82F6; 
              margin: 16px 0;
              border-radius: 4px;
            }
            .box-title {
              font-weight: 600;
              margin-bottom: 8px;
            }
            .step-item {
              display: flex;
              gap: 12px;
              margin-bottom: 12px;
            }
            .step-number {
              background: #0B5D6E;
              color: white;
              width: 24px;
              height: 24px;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 12px;
              font-weight: 600;
              flex-shrink: 0;
            }
            .footer { 
              margin-top: 32px; 
              padding-top: 16px; 
              border-top: 2px solid #e5e5e5; 
              font-size: 11px; 
              color: #666;
            }
            .footer p { margin: 4px 0; }
            .checklist-item {
              display: flex;
              align-items: center;
              gap: 8px;
              margin-bottom: 8px;
            }
            .checkbox {
              width: 16px;
              height: 16px;
              border: 2px solid #0B5D6E;
              border-radius: 3px;
            }
            @media print { 
              body { padding: 12px; }
              .page-break { page-break-before: always; }
            }
          </style>
        </head>
        <body>
          ${printContent}
          <div class="footer">
            <p><strong>Uro2Go Patient Education</strong> • Printed ${new Date().toLocaleDateString()}</p>
            <p>This information is for educational purposes only. Always follow your doctor's specific instructions.</p>
            <p>If you have questions, contact your healthcare provider.</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 250);
  };

  return { printRef, handlePrint };
};

// Render content based on topic type
const PelvicFloorPTPage = ({ content, lang = 'en' }) => {
  const data = content.overview[lang];
  const conditions = content.conditions;

  return (
    <div className="topic-content">
      {/* Overview Section */}
      <section className="content-section">
        <h2>What is Pelvic Floor Physical Therapy?</h2>
        <p>{data.whatIsIt}</p>
      </section>

      <section className="content-section">
        <h2>How It Helps</h2>
        <p>{data.howItHelps}</p>
      </section>

      <section className="content-section">
        <h2>What to Expect</h2>
        <ul className="styled-list">
          {data.whatToExpect.map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ul>
      </section>

      <CalloutBox type="info" title="When to Ask Your Doctor">
        <p>{data.whenToRequest}</p>
      </CalloutBox>

      {/* Condition-Specific Sections */}
      <h2 className="section-divider">Condition-Specific Exercises</h2>
      
      <CollapsibleSection 
        title="For Overactive Bladder (OAB)" 
        icon={<Info size={18} />}
        printable={true}
      >
        <p>{conditions.overactiveBladder[lang].howItHelps}</p>
        <h4>Home Exercises</h4>
        {conditions.overactiveBladder[lang].homeExercises.map((exercise, idx) => (
          <div key={idx} className="exercise-card">
            <h5>{exercise.name}</h5>
            <p>{exercise.description}</p>
            <span className="exercise-frequency"><Clock size={14} /> {exercise.frequency}</span>
          </div>
        ))}
      </CollapsibleSection>

      <CollapsibleSection 
        title="For Urinary Incontinence" 
        icon={<Info size={18} />}
        printable={true}
      >
        <p>{conditions.urinaryIncontinence[lang].howItHelps}</p>
        <h4>Home Exercises</h4>
        {conditions.urinaryIncontinence[lang].homeExercises.map((exercise, idx) => (
          <div key={idx} className="exercise-card">
            <h5>{exercise.name}</h5>
            <p>{exercise.description}</p>
            <span className="exercise-frequency"><Clock size={14} /> {exercise.frequency}</span>
          </div>
        ))}
      </CollapsibleSection>

      <CollapsibleSection 
        title="For Chronic Prostatitis / Pelvic Pain" 
        icon={<Info size={18} />}
        printable={true}
      >
        <p>{conditions.chronicProstatitis[lang].howItHelps}</p>
        <h4>Home Exercises</h4>
        {conditions.chronicProstatitis[lang].homeExercises.map((exercise, idx) => (
          <div key={idx} className="exercise-card">
            <h5>{exercise.name}</h5>
            <p>{exercise.description}</p>
            <span className="exercise-frequency"><Clock size={14} /> {exercise.frequency}</span>
          </div>
        ))}
      </CollapsibleSection>

      <CollapsibleSection 
        title="After Prostate Surgery" 
        icon={<Info size={18} />}
        printable={true}
      >
        <p>{conditions.postProstatectomy[lang].howItHelps}</p>
        <h4>Home Exercises</h4>
        {conditions.postProstatectomy[lang].homeExercises.map((exercise, idx) => (
          <div key={idx} className="exercise-card">
            <h5>{exercise.name}</h5>
            <p>{exercise.description}</p>
            <span className="exercise-frequency"><Clock size={14} /> {exercise.frequency}</span>
          </div>
        ))}
        <CalloutBox type="tip" title="Recovery Timeline">
          <p>{conditions.postProstatectomy[lang].timeline}</p>
        </CalloutBox>
      </CollapsibleSection>

      {/* FAQ Section */}
      <FAQBox questions={data.faq} />
    </div>
  );
};

const BCGTherapyPage = ({ content, lang = 'en', printRef }) => {
  const data = content[lang];

  return (
    <div className="topic-content">
      <div ref={printRef} className="printable-content">
        <div className="header">
          <h1>{data.title}</h1>
          <p className="subtitle">Patient Guide for BCG Immunotherapy</p>
        </div>

        <section className="content-section">
          <h2>Overview</h2>
          <p>{data.overview}</p>
        </section>

        <section className="content-section">
          <h2>{data.whoReceives.title}</h2>
          <p>{data.whoReceives.content}</p>
          <ul className="styled-list">
            {data.whoReceives.items.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        </section>

        <section className="content-section">
          <h2>{data.inductionVsMaintenance.title}</h2>
          <div className="comparison-grid">
            <div className="comparison-card">
              <h3>{data.inductionVsMaintenance.induction.title}</h3>
              <p>{data.inductionVsMaintenance.induction.description}</p>
              <div className="schedule-badge">
                <Clock size={14} /> {data.inductionVsMaintenance.induction.schedule}
              </div>
              <p className="purpose-text">{data.inductionVsMaintenance.induction.purpose}</p>
            </div>
            <div className="comparison-card">
              <h3>{data.inductionVsMaintenance.maintenance.title}</h3>
              <p>{data.inductionVsMaintenance.maintenance.description}</p>
              <div className="schedule-badge">
                <Clock size={14} /> {data.inductionVsMaintenance.maintenance.schedule}
              </div>
              <p className="purpose-text">{data.inductionVsMaintenance.maintenance.purpose}</p>
            </div>
          </div>
        </section>

        <CollapsibleSection 
          title={data.treatmentDay.title}
          icon={<BookOpen size={18} />}
          defaultOpen={true}
          printable={true}
        >
          <h4>Before Treatment</h4>
          <ul className="styled-list">
            {data.treatmentDay.beforeTreatment.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>

          <h4>During Treatment</h4>
          <StepList steps={data.treatmentDay.duringTreatment} />

          <h4>After Treatment</h4>
          <StepList steps={data.treatmentDay.afterTreatment} />
        </CollapsibleSection>

        <section className="content-section">
          <h2>{data.sideEffects.title}</h2>
          <CalloutBox type="warning" title="Common Side Effects">
            <ul>
              {data.sideEffects.common.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </CalloutBox>

          <CalloutBox type="emergency" title="When to Call Your Doctor">
            <ul>
              {data.sideEffects.whenToCall.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </CalloutBox>
        </section>

        <CollapsibleSection 
          title={data.precautions.title}
          icon={<AlertTriangle size={18} />}
          printable={true}
        >
          <ul className="styled-list warning-list">
            {data.precautions.items.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        </CollapsibleSection>

        <CollapsibleSection 
          title={data.followUp.title}
          icon={<Clock size={18} />}
          printable={true}
        >
          <div className="timeline-list">
            {data.followUp.schedule.map((item, idx) => (
              <div key={idx} className="timeline-item">
                <span className="timeline-timing">{item.timing}</span>
                <span className="timeline-purpose">{item.purpose}</span>
              </div>
            ))}
          </div>
          <p className="note-text">{data.followUp.note}</p>
        </CollapsibleSection>

        <CollapsibleSection 
          title={data.checklist.title}
          icon={<CheckCircle size={18} />}
          printable={true}
        >
          <div className="checklist">
            {data.checklist.items.map((item, idx) => (
              <div key={idx} className="checklist-item">
                <input type="checkbox" id={`check-${idx}`} />
                <label htmlFor={`check-${idx}`}>{item.item}</label>
              </div>
            ))}
          </div>
        </CollapsibleSection>
      </div>
    </div>
  );
};

const CatheterFlushingPage = ({ content, lang = 'en', printRef }) => {
  const data = content[lang];

  return (
    <div className="topic-content">
      <div ref={printRef} className="printable-content">
        <div className="header">
          <h1>{data.title}</h1>
          <p className="subtitle">Home Care Instructions</p>
        </div>

        <section className="content-section">
          <h2>Overview</h2>
          <p>{data.overview}</p>
        </section>

        <CalloutBox type="tip" title={data.whenToFlush.title}>
          <ul>
            {data.whenToFlush.items.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        </CalloutBox>

        <CalloutBox type="emergency" title={data.whenNotToFlush.title}>
          <ul>
            {data.whenNotToFlush.items.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        </CalloutBox>

        <CollapsibleSection 
          title={data.supplies.title}
          icon={<Info size={18} />}
          defaultOpen={true}
          printable={true}
        >
          <ul className="supplies-list">
            {data.supplies.items.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        </CollapsibleSection>

        <CollapsibleSection 
          title={data.steps.title}
          icon={<BookOpen size={18} />}
          defaultOpen={true}
          printable={true}
        >
          <StepList steps={data.steps.steps} />
        </CollapsibleSection>

        <CollapsibleSection 
          title={data.troubleshooting.title}
          icon={<HelpCircle size={18} />}
          printable={true}
        >
          <div className="troubleshooting-grid">
            {data.troubleshooting.problems.map((item, idx) => (
              <div key={idx} className="troubleshooting-item">
                <div className="problem"><AlertCircle size={16} /> {item.problem}</div>
                <div className="solution"><CheckCircle size={16} /> {item.solution}</div>
              </div>
            ))}
          </div>
        </CollapsibleSection>

        <CalloutBox type="emergency" title={data.warningSignsEmergency.title}>
          <ul>
            {data.warningSignsEmergency.items.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        </CalloutBox>
      </div>
    </div>
  );
};

const NephrostomyCarePage = ({ content, lang = 'en', printRef }) => {
  const data = content[lang];

  return (
    <div className="topic-content">
      <div ref={printRef} className="printable-content">
        <div className="header">
          <h1>{data.title}</h1>
          <p className="subtitle">Complete Home Care Guide</p>
        </div>

        <section className="content-section">
          <h2>What is a Nephrostomy Tube?</h2>
          <p>{data.whatIsIt}</p>
        </section>

        {data.whyNeeded && (
          <section className="content-section">
            <h2>{data.whyNeeded.title}</h2>
            <ul className="styled-list">
              {data.whyNeeded.items.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </section>
        )}

        <CollapsibleSection 
          title={data.dailyCare.title}
          icon={<CheckCircle size={18} />}
          defaultOpen={true}
          printable={true}
        >
          <StepList steps={data.dailyCare.steps} />
        </CollapsibleSection>

        {data.dressingChanges && (
          <CollapsibleSection 
            title={data.dressingChanges.title}
            icon={<Info size={18} />}
            printable={true}
          >
            <p><strong>Frequency:</strong> {data.dressingChanges.frequency}</p>
            <h4>Supplies Needed</h4>
            <ul className="supplies-list">
              {data.dressingChanges.supplies.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
            <h4>Steps</h4>
            <StepList steps={data.dressingChanges.steps.map(s => ({ title: s }))} />
          </CollapsibleSection>
        )}

        {data.bagEmptying && (
          <CollapsibleSection 
            title={data.bagEmptying.title}
            icon={<Info size={18} />}
            printable={true}
          >
            <StepList steps={data.bagEmptying.steps.map(s => ({ title: s }))} />
            {data.bagEmptying.note && (
              <p className="note-text">{data.bagEmptying.note}</p>
            )}
          </CollapsibleSection>
        )}

        {data.flushing && (
          <CollapsibleSection 
            title={data.flushing.title}
            icon={<AlertTriangle size={18} />}
            printable={true}
          >
            <CalloutBox type="warning">
              <p>{data.flushing.note}</p>
            </CalloutBox>
            <StepList steps={data.flushing.steps.map(s => ({ title: s }))} />
          </CollapsibleSection>
        )}

        <CalloutBox type="warning" title={data.redFlags.title}>
          <ul>
            {data.redFlags.items.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        </CalloutBox>

        <CalloutBox type="emergency" title={data.emergencySigns.title}>
          <ul>
            {data.emergencySigns.items.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        </CalloutBox>

        {data.exchangeTimeline && (
          <section className="content-section">
            <h2>{data.exchangeTimeline.title}</h2>
            <p>{data.exchangeTimeline.content}</p>
            <CalloutBox type="info">
              <p>{data.exchangeTimeline.note}</p>
            </CalloutBox>
          </section>
        )}
      </div>
    </div>
  );
};

// Main Topic Page Component
const EducationalTopicPage = () => {
  const { topicId } = useParams();
  const topic = topicData[topicId];
  const printRef = useRef(null);
  const [language, setLanguage] = useState('en');

  const toggleLanguage = () => {
    setLanguage(lang => lang === 'en' ? 'es' : 'en');
  };

  if (!topic) {
    return (
      <PageWrapper>
        <div className="error-page">
          <h1>Topic Not Found</h1>
          <p>The educational topic you&apos;re looking for doesn&apos;t exist.</p>
          <Link to="/education" className="btn-primary">
            <ArrowLeft size={18} /> Back to Education Library
          </Link>
        </div>
      </PageWrapper>
    );
  }

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const content = printRef.current?.innerHTML || '';
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${topic.title} - Uro2Go Patient Guide</title>
          <style>
            * { box-sizing: border-box; }
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              padding: 24px;
              max-width: 800px;
              margin: 0 auto;
              line-height: 1.6;
              color: #1a1a1a;
            }
            .header { 
              border-bottom: 3px solid #0B5D6E; 
              padding-bottom: 16px; 
              margin-bottom: 24px;
            }
            h1 { color: #0B5D6E; font-size: 24px; margin: 0 0 8px 0; }
            .subtitle { color: #666; font-size: 14px; }
            h2 { color: #0B5D6E; font-size: 18px; margin: 24px 0 12px 0; border-bottom: 1px solid #e5e5e5; padding-bottom: 8px; }
            h3, h4 { color: #333; font-size: 16px; margin: 16px 0 8px 0; }
            ul, ol { margin: 8px 0 16px 20px; padding: 0; }
            li { margin-bottom: 6px; }
            .callout-box { padding: 12px 16px; border-left: 4px solid #3B82F6; margin: 16px 0; border-radius: 4px; background: #EFF6FF; }
            .callout-warning { background: #FEF3C7; border-color: #F59E0B; }
            .callout-emergency { background: #FEE2E2; border-color: #DC2626; }
            .callout-tip { background: #ECFDF5; border-color: #10B981; }
            .footer { margin-top: 32px; padding-top: 16px; border-top: 2px solid #e5e5e5; font-size: 11px; color: #666; }
            @media print { body { padding: 12px; } }
          </style>
        </head>
        <body>
          ${content}
          <div class="footer">
            <p><strong>Uro2Go Patient Education</strong> • Printed ${new Date().toLocaleDateString()}</p>
            <p>This information is for educational purposes only. Always follow your doctor's specific instructions.</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 250);
  };

  const renderTopicContent = () => {
    switch (topicId) {
      case 'pelvic-floor-pt':
        return <PelvicFloorPTPage content={topic.content} lang={language} />;
      case 'bcg-therapy':
        return <BCGTherapyPage content={topic.content} lang={language} printRef={printRef} />;
      case 'catheter-flushing':
        return <CatheterFlushingPage content={topic.content} lang={language} printRef={printRef} />;
      case 'nephrostomy-care':
        return <NephrostomyCarePage content={topic.content} lang={language} printRef={printRef} />;
      default:
        return <p>Content not available.</p>;
    }
  };

  return (
    <PageWrapper>
      <Breadcrumbs items={[
        { label: 'Education Library', path: '/education' },
        { label: topic.title, path: `/education/${topicId}` }
      ]} />

      <div className="topic-page" data-testid="educational-topic-page">
        {/* Header */}
        <div className="topic-header">
          <Link to="/education" className="back-link">
            <ArrowLeft size={18} /> Back to Library
          </Link>
          <div className="topic-title-section">
            <span className="topic-icon">{topic.icon}</span>
            <div>
              <h1>{topic.title}</h1>
              <p className="topic-description">{topic.description}</p>
              <div className="topic-meta">
                <span><Clock size={14} /> {topic.readingTime} min read</span>
                <span className="divider">•</span>
                <span><BookOpen size={14} /> Patient Education</span>
              </div>
            </div>
          </div>
          <div className="topic-actions">
            <button 
              onClick={toggleLanguage}
              className="btn-secondary language-toggle-btn"
              data-testid="language-toggle"
            >
              <Globe size={18} /> {language === 'en' ? 'Español' : 'English'}
            </button>
            <button 
              onClick={handlePrint} 
              className="btn-secondary"
              data-testid="print-guide-btn"
            >
              <Printer size={18} /> Print Guide
            </button>
            <button 
              onClick={handlePrint} 
              className="btn-primary"
              data-testid="download-pdf-btn"
            >
              <Download size={18} /> Download PDF
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="topic-main">
          {renderTopicContent()}
        </div>

        {/* Related Videos Section */}
        <div className="topic-sidebar">
          <RelatedVideos 
            articleTitle={topic.title} 
            articleCategory={topic.category}
            articleTags={topic.relatedTags} 
            limit={3} 
          />
        </div>
      </div>
    </PageWrapper>
  );
};

export default EducationalTopicPage;

// Export topic data for use in Education Library
export { topicData };
